/**
 * One photo a COLLEAGUE sent, inside an outgoing bubble (Inc 8, D133).
 *
 * ⚠️ WHY THIS IS NOT AN `<img src={photoUrl}>`. It was, until staging proved otherwise: the send
 * worked, Booking.com showed the photo, and the PMS painted a broken image. Our buckets grant
 * anonymous read per PREFIX — `properties/*` (where automated check-in photos live) is public,
 * `inbox/*` is not — so the stored URL 403s in a browser. Opening that prefix was the one-line
 * alternative and was rejected: these are guest-conversation photos, and a public URL is viewable by
 * anyone who obtains it, forever. The bytes come through the authenticated API instead, exactly like
 * inbound guest photos in MessageAttachment.jsx.
 *
 * ⚠️ DELIBERATELY A SIBLING OF MessageAttachment, not a refactor of it. That component is live on
 * prod serving inbound photos; the blob lifecycle below is the same shape and could be shared, but
 * factoring it out would put a working path at risk to save thirty lines. The extraction is owed as
 * its own change, with its own tests — the same call made for sendClaim.js on the backend.
 *
 * Built not to throw: every failure degrades to a quiet label, because a broken-image icon in a
 * guest thread reads as "the PMS lost it" when the guest in fact has the photo.
 */
import React, { useState, useEffect, useRef } from 'react';
import { conversationApi } from '../../services/conversationApi';

/**
 * @param {string} conversationId
 * @param {string} sendId      the GuestAttachmentSend id — an ID, never a storage location
 * @param {string} [previewUrl] local blob for a photo sent in THIS view; shown while the fetch is in
 *                             flight so the bubble never blinks empty on the send the operator just made
 */
export default function SentPhoto({ conversationId, sendId, previewUrl }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  // Held in a ref as well as state so cleanup revokes the CURRENT url without listing it as an
  // effect dependency — which would re-run the fetch on every url change and loop.
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Drop what is on screen the moment the TARGET changes: the cleanup below revokes the old object
    // URL, so leaving it in state would point the bubble at a released blob until the new one lands.
    setUrl(null);
    setFailed(false);

    if (!conversationId || !sendId) return undefined;

    conversationApi
      .replyPhotoImage(conversationId, sendId)
      .then((blob) => {
        // A thread switched mid-flight must not paint into the new thread, nor leak its blob.
        if (cancelled) return;
        try {
          const objectUrl = URL.createObjectURL(blob);
          urlRef.current = objectUrl;
          setUrl(objectUrl);
        } catch {
          setFailed(true);
        }
      })
      .catch(() => {
        // 404 (row or object gone), 410 (bytes swept at 12 months), 502 (storage unreachable). All
        // three mean the same thing to the operator: the photo cannot be shown right now.
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      // ⚠️ NOT optional. An inbox tab stays open all shift; an un-revoked object URL is a leak that
      // grows with every photo scrolled past.
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [conversationId, sendId]);

  // The local preview outlives the fetch when the operator just sent this photo, so it covers the
  // in-flight gap AND a fetch that failed outright. `url` wins when present: it is the stored
  // object, and it is what a reload will show.
  const src = url || previewUrl || null;

  if (src) {
    return (
      <img
        className="inbox-reply-photo"
        // Keyed by source so a fallback remounts cleanly rather than reusing a broken <img> state.
        key={src}
        src={src}
        alt="صورة أُرسلت للضيف"
        loading="lazy"
        // The failure that actually happens: bytes arrived but do not decode. That raises no React
        // error, so no boundary above would see it — without this the operator gets a broken-image
        // icon, which reads as a PMS fault rather than a display one.
        onError={() => {
          console.warn(`[INBOX-REPLY-PHOTO] image failed to decode — send ${sendId}`);
          // Drop the fetched object so a local preview, if we have one, still shows. With none, this
          // falls through to the label below.
          if (urlRef.current) {
            URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
          }
          setUrl(null);
          setFailed(true);
        }}
      />
    );
  }

  if (failed) {
    // Honest, and specific: the guest HAS this photo. The bubble must not imply the send failed.
    return (
      <span className="inbox-attachment">
        <i className="bi bi-image" aria-hidden="true"></i>
        تعذّر عرض الصورة — وصلت إلى الضيف
      </span>
    );
  }

  // Reserve the space rather than collapsing it, so the thread does not jump when the photo lands.
  return (
    <div className="inbox-attachment-loading" role="status" aria-label="جارٍ تحميل الصورة">
      <i className="bi bi-image" aria-hidden="true"></i>
    </div>
  );
}
